import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Leaf } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [interval, setInterval] = useState("monthly"); // "monthly" | "annual"
  const [subInfo, setSubInfo] = useState(null); // { active, plan, interval }
  const [showAll, setShowAll] = useState(false);
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const focusPlanId = searchParams.get("plan");

  const loadStatus = async () => {
    if (!user) return;
    try {
      const r = await api.get("/subscription/status");
      setSubInfo({ ...r.data.subscription, interval: r.data.interval || "monthly" });
    } catch (err) { console.error("Failed to load subscription status:", err); }
  };

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data));
  }, []);

  useEffect(() => { loadStatus(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyEquiv = (pkg, intv) =>
    intv === "annual" ? pkg.price_annual / 12 : pkg.price_monthly;

  const hasActiveSub = !!(subInfo?.active && subInfo?.plan);
  const currentPkg = subInfo?.plan ? packages.find((p) => p.id === subInfo.plan) : null;

  const subscribe = async (packageId) => {
    if (!user) {
      nav(`/signup?plan=${packageId}`);
      return;
    }
    setBusy(packageId);
    try {
      const origin_url = window.location.origin;
      const r = await api.post("/checkout/session", {
        package_id: packageId,
        origin_url,
        interval,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(null);
    }
  };

  const changePlan = async (packageId, name) => {
    setBusy(packageId);
    try {
      const { data } = await api.post("/subscription/change", { package_id: packageId, interval });
      const past = data.direction === "upgrade" ? "Upgraded" : data.direction === "downgrade" ? "Downgraded" : "Switched";
      toast.success(`${past} to ${name} (${interval}). Your access is updated.`);
      await refresh();
      await loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change plan");
    } finally {
      setBusy(null);
    }
  };

  // Returns { label, action, disabled } for a plan card given current sub state.
  const ctaFor = (p) => {
    if (!hasActiveSub) {
      return { label: `Choose ${p.name}`, onClick: () => subscribe(p.id), disabled: false };
    }
    const sameTier = subInfo.plan === p.id;
    const sameInterval = (subInfo.interval || "monthly") === interval;
    if (sameTier && sameInterval) {
      return { label: "Current plan", onClick: null, disabled: true };
    }
    if (sameTier) {
      // Same tier, different billing interval.
      return { label: `Switch to ${interval} billing`, onClick: () => changePlan(p.id, p.name), disabled: false };
    }
    const target = monthlyEquiv(p, interval);
    const cur = currentPkg ? monthlyEquiv(currentPkg, subInfo.interval || "monthly") : 0;
    const verb = target >= cur ? "Upgrade to" : "Downgrade to";
    return { label: `${verb} ${p.name}`, onClick: () => changePlan(p.id, p.name), disabled: false };
  };

  const focusPkg = focusPlanId ? packages.find((p) => p.id === focusPlanId) : null;
  const isFocused = !!focusPkg && !hasActiveSub && !showAll;

  const renderPlanCard = (p) => {
    const featured = p.id === "vital";
    const monthlyPrice = p.price_monthly;
    const annualPrice = p.price_annual;
    const displayPrice = interval === "annual" ? Math.round(annualPrice / 12) : monthlyPrice;
    const billedNote =
      interval === "annual"
        ? `$${annualPrice} billed yearly · save $${monthlyPrice * 2}`
        : "Billed monthly";
    const cta = ctaFor(p);

    return (
      <div
        key={p.id}
        data-testid={`plan-card-${p.id}`}
        className={`relative rounded-3xl p-8 border ${featured ? "bg-[#2D322B] text-[#F9F6F0] border-[#2D322B]" : "bg-[#F1ECE4] border-[#D1C7B8] text-[#2D322B]"}`}
      >
        {featured && (
          <span className="absolute -top-3 left-8 bg-[#D49A44] text-[#2D322B] text-xs uppercase tracking-widest px-3 py-1 rounded-full">Most chosen</span>
        )}
        <p className={`label-eyebrow ${featured ? "!text-[#D49A44]" : ""}`}>{p.name}</p>
        <p className="font-serif text-5xl mt-4" data-testid={`plan-price-${p.id}`}>
          ${displayPrice}
          <span className={`text-base ${featured ? "text-[#F9F6F0]/70" : "text-[#5C6359]"}`}>/mo</span>
        </p>
        <p className={`text-xs mt-1 ${featured ? "text-[#F9F6F0]/70" : "text-[#5C6359]"}`} data-testid={`plan-billed-${p.id}`}>
          {billedNote}
        </p>
        <p className={`text-sm mt-4 leading-relaxed ${featured ? "text-[#F9F6F0]/80" : "text-[#5C6359]"}`}>
          {p.description}
        </p>
        <ul className="mt-8 space-y-3">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm">
              <Check className={`w-4 h-4 mt-0.5 shrink-0 ${featured ? "text-[#D49A44]" : "text-[#C05C42]"}`} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={cta.onClick || undefined}
          disabled={busy === p.id || cta.disabled}
          data-testid={`subscribe-btn-${p.id}`}
          className={`mt-10 w-full ${featured ? "btn-primary !bg-[#D49A44] hover:!bg-[#bf8a3a] !text-[#2D322B]" : "btn-primary"}`}
        >
          {busy === p.id ? "Working…" : cta.label}
        </button>
        {p.id === "sovereign" && (
          <p
            data-testid="sovereign-perk-line"
            className="mt-4 flex items-center justify-center gap-2 text-xs italic text-[#5C6359]"
          >
            <Leaf className="w-3.5 h-3.5 text-[#C05C42]" />
            Your first ceremony free
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-10 max-w-6xl mx-auto">
        <p className="label-eyebrow mb-4">Membership</p>
        {isFocused ? (
          <>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D322B] max-w-3xl" data-testid="focused-heading">
              Start with {focusPkg.name}.
            </h1>
            <p className="mt-6 text-base text-[#5C6359] max-w-2xl leading-relaxed">
              The path most members begin on — your AI Concierge, biomarker tracking and personalized
              longevity protocols, plus everything in the foundational library. Cancel anytime.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#2D322B] max-w-3xl">
              Three paths into Badi.
            </h1>
            <p className="mt-6 text-base text-[#5C6359] max-w-2xl leading-relaxed">
              All tiers include the foundational library and rotate the same root teachings.
              Vital adds the AI Concierge & biomarker tracking. Sovereign opens the door to
              practitioner introductions and live ceremonies.
            </p>
          </>
        )}

        {/* Billing interval toggle */}
        <div className="mt-10 flex items-center gap-4" data-testid="billing-toggle">
          <div className="inline-flex p-1 rounded-full bg-[#F1ECE4] border border-[#D1C7B8]">
            <button
              onClick={() => setInterval("monthly")}
              data-testid="toggle-monthly"
              className={`px-6 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${interval === "monthly" ? "bg-[#2D322B] text-[#F9F6F0]" : "text-[#2D322B] hover:text-[#C05C42]"}`}
            >
              Monthly
            </button>
            <button
              onClick={() => setInterval("annual")}
              data-testid="toggle-annual"
              className={`px-6 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${interval === "annual" ? "bg-[#2D322B] text-[#F9F6F0]" : "text-[#2D322B] hover:text-[#C05C42]"}`}
            >
              Annual
            </button>
          </div>
          <span
            className={`text-xs uppercase tracking-widest transition-opacity ${interval === "annual" ? "opacity-100" : "opacity-0"}`}
            data-testid="annual-savings-pill"
          >
            <span className="inline-block bg-[#D49A44] text-[#2D322B] px-3 py-1 rounded-full">Save 2 months · 17% off</span>
          </span>
        </div>
      </section>

      {isFocused ? (
        <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-md mx-auto" data-testid="focused-plan-section">
          {renderPlanCard(focusPkg)}
          <button
            onClick={() => setShowAll(true)}
            data-testid="compare-all-tiers"
            className="mt-8 w-full text-center text-xs uppercase tracking-widest text-[#5C6359] hover:text-[#C05C42] transition-colors"
          >
            Compare all three tiers
          </button>
        </section>
      ) : (
        <section className="px-6 sm:px-12 lg:px-20 pb-24 max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {packages.map((p) => renderPlanCard(p))}
        </section>
      )}

      <Footer />
    </div>
  );
}
